import { Injectable, inject } from '@angular/core';
import { ModalOptions, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { describeConfirmModal } from '@core/utils';

/**
 * Wraps NzModalService.confirm() and patches the rendered DOM to satisfy
 * strict accessibility audits.
 *
 * Patches applied:
 *   1. <nz-modal-confirm-container> gets an aria-label, and aria-describedby
 *      pointing at the message, before focus moves in
 *        - Resolves "Modal has no accessible name"
 *        - Screen readers announce the message when focus lands on a button
 *   2. CDK focus-trap anchors (the two empty <div tabindex="0"> tab-catchers
 *      around the modal) are removed from the DOM entirely.
 *        - Resolves "aria-hidden on focusable"
 *        - Resolves "Non-active element in tab order"
 *      Trade-off: Tab key from the last focusable element in the modal
 *      can land on background content. For confirm dialogs (2 buttons)
 *      this is acceptable. The modal is still visually unmistakable, and
 *      Escape / Cancel / OK / etc. still close it normally.
 *
 * Pass `ariaLabel` explicitly when nzTitle is a TemplateRef or otherwise
 * not a plain string (e.g. the "Please Note" popup whose title is inside
 * an ng-template).
 */
@Injectable({ providedIn: 'root' })
export class AccessibleConfirmService {
  private modal = inject(NzModalService);

  confirm(config: ModalOptions, ariaLabel?: string): NzModalRef {
    const labelText = ariaLabel ?? this.deriveLabelFromTitle(config.nzTitle);
    const ref = this.modal.confirm(config);
    describeConfirmModal(ref, labelText);

    ref.afterOpen.subscribe(() => {
      // Wait one tick so the focus trap has finished setting up, then patch.
      setTimeout(() => this.removeFocusTrapAnchors(ref));
    });

    return ref;
  }

  private deriveLabelFromTitle(title: unknown): string {
    return typeof title === 'string' ? title : 'Dialog';
  }

  private removeFocusTrapAnchors(ref: NzModalRef): void {
    // Remove the CDK focus-trap anchors that are siblings of this container.
    // Scoped to overlayWrapper so we only touch this specific modal instance.
    const overlayWrapper = ref.containerInstance.getNativeElement().parentElement;
    if (!overlayWrapper) return;

    const anchors = overlayWrapper.querySelectorAll<HTMLElement>('.cdk-focus-trap-anchor');
    anchors.forEach(anchor => anchor.remove());
  }
}
